import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import { trackProductEvent } from "@/lib/productAnalytics";
import { getGoogleMeetOAuthNotice } from "@/lib/googleMeetOAuth";
import {
  ArrowClockwise,
  CheckCircle,
  Clock,
  GoogleLogo,
  LinkBreak,
  SpinnerGap,
  Trash,
  Users,
  WarningCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

function formatDateTime(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleString([], {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function meetingDuration(seconds) {
  const numeric = Number(seconds);
  if (!Number.isFinite(numeric) || numeric <= 0) return "";
  const minutes = Math.max(1, Math.round(numeric / 60));
  return `${minutes} min`;
}

function initialDraft(meeting) {
  return {
    title: meeting.title || "Google Meet",
    customer_name:
      meeting.recommended_customer_name
      || (meeting.candidate_customer_names?.length === 1 ? meeting.candidate_customer_names[0] : ""),
  };
}

export default function IntegrationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [inbox, setInbox] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [action, setAction] = useState("");
  const oauthOutcome = new URLSearchParams(location.search).get("google_meet");
  const oauthNotice = getGoogleMeetOAuthNotice(oauthOutcome, status || {});

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    setPageError("");
    try {
      const [statusResponse, inboxResponse, customersResponse] = await Promise.all([
        api.get("/integrations/google-meet/status"),
        api.get("/integrations/google-meet/inbox"),
        api.get("/customers"),
      ]);
      const rows = Array.isArray(inboxResponse.data) ? inboxResponse.data : [];
      setStatus(statusResponse.data || {});
      setInbox(rows);
      setCustomers(Array.isArray(customersResponse.data) ? customersResponse.data : []);
      setDrafts((current) => {
        const next = { ...current };
        rows.forEach((meeting) => {
          if (!next[meeting.id]) next[meeting.id] = initialDraft(meeting);
        });
        return next;
      });
      return statusResponse.data || {};
    } catch (error) {
      setPageError(getActionableError(error, "Callsect could not load your meeting integrations."));
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const outcome = oauthOutcome;
    if (!outcome) return;
    let active = true;
    const verifyOutcome = async () => {
      const serverStatus = await load({ quiet: true });
      if (!active) return;
      if (outcome === "connected" && serverStatus?.connected && serverStatus?.initial_sync_succeeded) {
        toast.success("Google Meet connected");
        trackProductEvent("google_meet_connected", { section: "meeting_integrations", outcome: "success" });
      }
      navigate("/integrations", { replace: true });
    };
    verifyOutcome();
    return () => { active = false; };
  }, [load, navigate, oauthOutcome]);

  useEffect(() => {
    const resetInterruptedConnect = () => setAction((current) => (current === "connect" ? "" : current));
    const onVisibility = () => {
      if (document.visibilityState === "visible") resetInterruptedConnect();
    };
    window.addEventListener("pageshow", resetInterruptedConnect);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pageshow", resetInterruptedConnect);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!status?.connected) return undefined;
    const timer = window.setInterval(() => load({ quiet: true }), 15000);
    return () => window.clearInterval(timer);
  }, [load, status?.connected]);

  const customerNames = useMemo(
    () => [...new Set(customers.map((customer) => customer.name).filter(Boolean))],
    [customers],
  );

  const connect = async () => {
    setAction("connect");
    try {
      const { data } = await api.get("/integrations/google-meet/connect");
      if (!data?.authorization_url) throw new Error("Missing Google authorization URL");
      window.location.assign(data.authorization_url);
    } catch (error) {
      setAction("");
      toast.error(getActionableError(error, "Google Meet could not be connected."));
    }
  };

  const sync = async () => {
    setAction("sync");
    try {
      const { data } = await api.post("/integrations/google-meet/sync");
      if (data?.in_progress) toast.info("A Google Meet sync is already running.");
      else if (data?.imported) toast.success(`${data.imported} meeting${data.imported === 1 ? "" : "s"} sent to Callsect.`);
      else if (data?.inbox) toast.success(`${data.inbox} meeting${data.inbox === 1 ? "" : "s"} ready for customer confirmation.`);
      else toast.info("Google Meet is up to date.");
      trackProductEvent("google_meet_synced", {
        section: "meeting_integrations",
        outcome: data?.imported ? "auto_matched" : data?.inbox ? "inbox" : "no_change",
        call_count: Number(data?.imported || 0),
      });
      await load({ quiet: true });
    } catch (error) {
      toast.error(getActionableError(error, "Google Meet could not be synced."));
      await load({ quiet: true });
    } finally {
      setAction("");
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect Google Meet? Existing Callsect conversations will stay available.")) return;
    setAction("disconnect");
    try {
      await api.delete("/integrations/google-meet");
      toast.success("Google Meet disconnected");
      setStatus((current) => ({ ...current, connected: false, status: "disconnected", email: null }));
      trackProductEvent("google_meet_disconnected", { section: "meeting_integrations" });
    } catch (error) {
      toast.error(getActionableError(error, "Google Meet could not be disconnected."));
    } finally {
      setAction("");
    }
  };

  const updateDraft = (meetingId, field, value) => {
    setDrafts((current) => ({
      ...current,
      [meetingId]: { ...(current[meetingId] || {}), [field]: value },
    }));
  };

  const confirmMeeting = async (meeting) => {
    if (meeting.processing_blocked) {
      toast.error(meeting.speaker_identity_message || "Callsect cannot safely distinguish YOU from CLIENT in this transcript.");
      return;
    }
    const draft = drafts[meeting.id] || initialDraft(meeting);
    if (!draft.customer_name?.trim()) {
      toast.error("Choose or enter the customer for this meeting.");
      return;
    }
    setAction(`confirm:${meeting.id}`);
    try {
      await api.post(`/integrations/google-meet/inbox/${meeting.id}/confirm`, {
        customer_name: draft.customer_name.trim(),
        title: draft.title?.trim() || undefined,
      });
      toast.success("Meeting sent to the Callsect workflow");
      trackProductEvent("google_meet_customer_confirmed", {
        section: "meeting_inbox",
        outcome: "processing_started",
      });
      navigate("/dashboard");
    } catch (error) {
      toast.error(getActionableError(error, "This meeting could not be sent to Callsect."));
      setAction("");
    }
  };

  const dismissMeeting = async (meeting) => {
    if (!window.confirm("Remove this transcript from review? It will not be imported later.")) return;
    setAction(`dismiss:${meeting.id}`);
    try {
      await api.delete(`/integrations/google-meet/inbox/${meeting.id}`);
      setInbox((current) => current.filter((item) => item.id !== meeting.id));
      toast.success("Meeting removed");
    } catch (error) {
      toast.error(getActionableError(error, "This meeting could not be removed."));
    } finally {
      setAction("");
    }
  };

  return (
    <div className="integrations-page min-h-screen">
      <Nav />
      <main className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 sm:py-10 lg:px-8 lg:py-14 product-page-enter">
                <header className="integrations-hero">
          <div>
            <div className="brand-eyebrow">YOUR INTEGRATIONS</div>
            <h1>Integrations</h1>
            <p>
              Connect services that bring your conversations into Callsect.
            </p>
          </div>
          <div className="upload-hero__flow integrations-hero__flow" aria-label="Automatic meeting workflow">
            <span><strong>1</strong> Transcript arrives</span>
            <span><strong>2</strong> Review if needed</span>
            <span><strong>3</strong> Callsect analyzes</span>
          </div>
        </header>

        {pageError && (
          <div className="integration-alert integration-alert--error" role="alert">
            <WarningCircle size={20} weight="duotone" />
            <span>{pageError}</span>
            <button type="button" onClick={() => load()}><ArrowClockwise size={16} /> Retry</button>
          </div>
        )}

        <section className="integration-card surface-card" aria-label="Google Meet integration">
          <div className="integration-card__identity">
            <span className="integration-card__logo"><GoogleLogo size={30} weight="bold" /></span>
            <div>
              <h2>Connect Google Meet</h2>
              <p>Automatically bring your meeting transcripts into Callsect after the call.</p>
            </div>
          </div>

          {loading ? (
            <div className="integration-card__loading"><SpinnerGap className="is-spinning" size={22} /> Checking connection…</div>
          ) : !status?.configured ? (
            <div className="integration-card__state integration-card__state--warning">
              <strong>Deployment setup required</strong>
              <span>Add the Google web OAuth client ID, secret, and callback URI to the backend environment.</span>
            </div>
          ) : status?.connected ? (
            <div className="integration-card__connected">
              <div className="integration-card__state integration-card__state--connected">
                <CheckCircle size={20} weight="fill" />
                <span><strong>Connected</strong><small>{status.email || status.display_name}</small></span>
              </div>
              <div className="integration-card__sync-meta">
                <Clock size={16} />
                {status.last_sync_at ? `Last checked ${formatDateTime(status.last_sync_at)}` : "First sync is starting"}
              </div>
              {status.last_sync_error && (
                <div className="integration-inline-error"><WarningCircle size={16} /> {status.last_sync_error}</div>
              )}
              <div className="integration-card__actions">
                <button type="button" className="integration-button integration-button--primary" onClick={sync} disabled={Boolean(action)}>
                  {action === "sync" ? <SpinnerGap className="is-spinning" size={17} /> : <ArrowClockwise size={17} />}
                  Sync now
                </button>
                <button type="button" className="integration-button" onClick={disconnect} disabled={Boolean(action)}>
                  <LinkBreak size={17} /> Disconnect
                </button>
              </div>
              <p className="integration-card__automation-note">
                Callsect checks automatically every {Math.max(1, Math.round((status.sync_interval_seconds || 300) / 60))} minutes.
                Manual sync is only a fallback.
              </p>
            </div>
          ) : (
            <div className="integration-card__connect">
              <div className="integration-card__state">
                <strong>{status?.authorization_pending ? "Authorisation incomplete" : status?.status === "missing_scope" ? "Meet permission missing" : status?.status === "sync_failed" ? "First sync failed" : "Not connected"}</strong>
              </div>
              <button type="button" className="integration-button integration-button--primary" onClick={connect} disabled={action === "connect"}>
                {action === "connect" ? <SpinnerGap className="is-spinning" size={17} /> : <GoogleLogo size={17} weight="bold" />}
                Connect Google Meet
              </button>
              {(oauthNotice || ["needs_reconnect", "missing_scope", "sync_failed"].includes(status?.status)) && (
                <div className="integration-inline-error" role="alert"><WarningCircle size={16} /> {oauthNotice || status.last_sync_error || "Reconnect Google Meet and try again."}</div>
              )}
            </div>
          )}
        </section>


        <section className="meeting-inbox" aria-labelledby="meeting-inbox-title">
          <div className="meeting-inbox__header">
            <div>
              <span className="brand-eyebrow">MEETINGS TO REVIEW</span>
              <h2 id="meeting-inbox-title">Needs your attention</h2>
              <p>
                If Callsect can't confidently identify who's who in a meeting, it'll appear here for you to review.
              </p>
            </div>
            {inbox.length > 0 && <span className="meeting-inbox__count">{inbox.length} to review</span>}
          </div>

          {!loading && inbox.length === 0 ? (
            <div className="meeting-inbox__empty surface-card">
              <CheckCircle size={25} weight="duotone" />
              <div><strong>You're all caught up.</strong><span>Meetings that need your input will appear here.</span></div>
            </div>
          ) : (
            <div className="meeting-inbox__list">
              {inbox.map((meeting) => {
                const draft = drafts[meeting.id] || initialDraft(meeting);
                const confirming = action === `confirm:${meeting.id}`;
                const speakerMappingBlocked = Boolean(meeting.processing_blocked);
                return (
                  <article
                    key={meeting.id}
                    className={`meeting-inbox-card surface-card${speakerMappingBlocked ? " meeting-inbox-card--blocked" : ""}`}
                  >
                    <div className="meeting-inbox-card__meta">
                      <span>{formatDateTime(meeting.occurred_at)}</span>
                      {meetingDuration(meeting.duration) && <span>{meetingDuration(meeting.duration)}</span>}
                      <span>{meeting.transcript_preview}</span>
                    </div>
                    <div className="meeting-inbox-card__participants">
                      <Users size={17} weight="duotone" />
                      {(meeting.participants || []).map((participant) => participant.display_name).filter(Boolean).join(", ") || "Participant names unavailable"}
                    </div>
                    {speakerMappingBlocked && (
                      <div className="meeting-inbox-card__diagnostic" role="alert">
                        <WarningCircle size={20} weight="fill" />
                        <div>
                          <strong>We need your help with this meeting.</strong>
                          <span>Callsect couldn't confidently identify who's who, so it hasn't analyzed the conversation.</span>
                        </div>
                      </div>
                    )}
                    <div className="meeting-inbox-card__fields">
                      <label>
                        <span>Conversation title</span>
                        <input
                          value={draft.title || ""}
                          onChange={(event) => updateDraft(meeting.id, "title", event.target.value)}
                          maxLength={180}
                        />
                      </label>
                      <label>
                        <span>Customer</span>
                        <input
                          value={draft.customer_name || ""}
                          onChange={(event) => updateDraft(meeting.id, "customer_name", event.target.value)}
                          list="integration-customer-names"
                          placeholder="Select an existing relationship or enter a customer"
                          maxLength={200}
                          disabled={speakerMappingBlocked}
                        />
                      </label>
                    </div>
                    {meeting.last_error && <div className="integration-inline-error"><WarningCircle size={16} /> {meeting.last_error}</div>}
                    <div className="meeting-inbox-card__actions">
                      <button
                        type="button"
                        className="integration-button integration-button--primary"
                        onClick={() => confirmMeeting(meeting)}
                        disabled={Boolean(action) || speakerMappingBlocked}
                      >
                        {confirming ? <SpinnerGap className="is-spinning" size={17} /> : <CheckCircle size={17} />}
                        {speakerMappingBlocked ? "Review required" : "Confirm and analyze"}
                      </button>
                      <button type="button" className="integration-button integration-button--danger" onClick={() => dismissMeeting(meeting)} disabled={Boolean(action)}>
                        <Trash size={16} /> Dismiss
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
        <datalist id="integration-customer-names">
          {customerNames.map((name) => <option key={name} value={name} />)}
        </datalist>
      </main>
    </div>
  );
}
